//lib
import React, { useCallback, useContext, useEffect, useState } from "react";

// js-utils
import { isEmpty, safeJsonParse } from "@swiggy-private/js-utils";

// context
import Blaze from "@swiggy-private/blaze.js";
import {
    BottomBarContext,
    ScrolledStateContext,
    SwiggyContextHeader,
    TableContext,
    useDigitalMenuConfig,
} from "@swiggy-private/portal-dineout-context";

// next
import { useRouter } from "next/compat/router";

// component
import { getBridgeSdk, isApp } from "utils";
import { REDIRECTIONTYPES } from "utils/types";
import AgeGate from "../../../components/AgeGate";
import CurtainRaiser from "../../../components/CurtainRaiser/CurtainRaiser";
import Menu from "../../../components/Menu";
import { RECORD_PERCIEVED_TIME_EVENTS } from "../../../constants/menu";
import { useMenuPrefetch } from "../../../context/MenuPrefetchContext";
import { useHardwareBackHandler } from "../../../hooks";
import { useKeyboardState } from "../../../hooks/useKeyboardState";
import QrLayout from "../../../layouts/qr";

// styled
import { Container, LandingMain } from "../../../styles/home";
import { getDrinkingAgeConsent } from "../../../utils/drinkingAgeConsent";

// defs
import type { NextPageWithLayout } from "../../_app";

const MenuPage: NextPageWithLayout = () => {
    const router = useRouter();
    useKeyboardState();
    const { swiggyHeaders } = useContext(SwiggyContextHeader);
    const { isVisible: hasBottomBar, setIsVisible: setHasBottomBar } = useContext(BottomBarContext);
    const { tableId = "" } = useContext(TableContext);
    const { config } = useDigitalMenuConfig();
    const { refetchWithConsent } = useMenuPrefetch();
    const [curtainRaiserReady, setCurtainRaiserReady] = useState(false);
    const [showCurtainRaiser, setShowCurtainRaiser] = useState(false);
    const [isCurtainRaiserShown, setIsCurtainRaiserShown] = useState(false);

    // Age/legal-drinking-age consent gate. `null` = still deciding whether to show it.
    const [showAgeGate, setShowAgeGate] = useState<boolean | null>(null);

    useHardwareBackHandler(REDIRECTIONTYPES.MENU);

    const legalDrinkingAgeConfig = config?.legalDrinkingAgeConfig;

    // Resolves whether the consent gate should show: user must re-answer unless they
    // already answered (agree or decline) within the configured window.
    const resolveAgeGate = useCallback(async (): Promise<boolean> => {
        if (!legalDrinkingAgeConfig?.isEnabled) return false;
        const priorConsent = await getDrinkingAgeConsent(tableId, legalDrinkingAgeConfig.windowTimeInSeconds);
        if (priorConsent === false) {
            // Remembered decline — skip the gate, but correct the optimistic consentLDA=true
            // guess now. Menu/CurtainRaiser already show their own loading state until it lands.
            try {
                await refetchWithConsent(false);
                return false;
            } catch {
                // Correction failed — the optimistic consentLDA=true data is still what's in
                // state (wrong for a decline). Fall back to showing the gate so the user can
                // retry via its own loader/retry handling.
                return true;
            }
        }
        // No answer yet (show the gate), or a remembered agree — the optimistic guess
        // already matches consentLDA=true, so no correction is needed.
        return priorConsent === null;
    }, [legalDrinkingAgeConfig, tableId, refetchWithConsent]);

    useEffect(() => {
        if (!config) return;
        let cancelled = false;
        resolveAgeGate().then((shouldShow) => {
            if (!cancelled) setShowAgeGate(shouldShow);
        });
        return () => {
            cancelled = true;
        };
    }, [config, resolveAgeGate]);

    useEffect(() => {
        const menuEventLoaded = safeJsonParse(
            sessionStorage?.getItem?.(RECORD_PERCIEVED_TIME_EVENTS.MENU_LOAD_EVENT) || "false",
        );
        if (menuEventLoaded) {
            return;
        }
        Blaze.recordTimeFromAppStart(RECORD_PERCIEVED_TIME_EVENTS.MENU_LOAD_EVENT);
        sessionStorage?.setItem?.(RECORD_PERCIEVED_TIME_EVENTS.MENU_LOAD_EVENT, "true");
    }, []);

    if (!router?.isReady || (isApp() && isEmpty(swiggyHeaders))) {
        return <></>;
    }

    return (
        <ScrolledStateContext.Provider
            value={{
                sdk: getBridgeSdk(),
                router,
                isApp: isApp(),
            }}
        >
            <Container hasBottomBar={hasBottomBar}>
                <LandingMain>
                    {showAgeGate && legalDrinkingAgeConfig && (
                        <AgeGate config={legalDrinkingAgeConfig} onResolved={() => setShowAgeGate(false)} />
                    )}
                    {/* Mount the curtain only once the consent decision is resolved, so it
                        reveals the data matching the user's answer (no wrong-data flash). */}
                    {showAgeGate === false && (
                        <CurtainRaiser
                            show={showCurtainRaiser}
                            setShow={setShowCurtainRaiser}
                            ready={curtainRaiserReady}
                            setReady={setCurtainRaiserReady}
                            setIsCurtainRaiserShown={setIsCurtainRaiserShown}
                            setHasBottomBar={setHasBottomBar}
                        />
                    )}
                    <Menu setHasBottomBar={setHasBottomBar} isCurtainRaiserShown={isCurtainRaiserShown} />
                </LandingMain>
            </Container>
        </ScrolledStateContext.Provider>
    );
};

MenuPage.getLayout = function getLayout(page: React.ReactElement) {
    return <QrLayout>{page}</QrLayout>;
};

export default MenuPage;
